import { z } from "zod";
import {
  LandingModeSchema,
  OrganizationPlanSchema,
} from "../entities/organization.js";
import {
  HourExceptionListSchema,
  StoreProfileSchema,
  TimeZoneSchema,
} from "../entities/storeProfile.js";
import { ThemeSchema } from "../entities/theme.js";

/**
 * Payload for `POST /stores` — the platform-admin-only endpoint that
 * provisions a new store end-to-end (organization, subdomain, starter menu
 * categories and an owner invitation). Phase 2's admin UI builds its
 * "create a store" form from this same schema, so it is the single source
 * of truth for the shape rather than something inlined in the route.
 */
export const CreateStoreRequestSchema = z.object({
  name: z.string().min(2).max(128),
  slug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9-]+$/, "slug must be lowercase letters, digits and hyphens only"),
  ownerEmail: z.string().email(),
  plan: OrganizationPlanSchema.optional(),
  requestId: z.string().uuid().optional(),
});
export type CreateStoreRequest = z.infer<typeof CreateStoreRequestSchema>;

export const CreateStoreResponseSchema = z.object({
  organizationId: z.string().uuid(),
  hostname: z.string().min(1).max(253),
  invitationId: z.string().uuid(),
  siteUrl: z.string().url(),
});
export type CreateStoreResponse = z.infer<typeof CreateStoreResponseSchema>;

export const StoreStatusSchema = z.enum(["active", "blocked"]);
export type StoreStatus = z.infer<typeof StoreStatusSchema>;

/**
 * What `GET /stores/current` answers with — the whole of what a landing page
 * needs to render a store, resolved from the request's `Host` header before
 * anyone has signed in.
 *
 * Deliberately narrow. This route is public, so the response is the public
 * face of a store and nothing else: no `plan`, no block reason, no staff, no
 * billing state. Those exist on the organization and are readable by an
 * authenticated caller through other routes; putting them here would publish
 * every store's commercial terms to anyone who can resolve its hostname.
 * `status` is the exception, so a blocked store can show a holding page.
 *
 * `profile` and `theme` are always present, never null. A store that has
 * filled in nothing gets the defaults out of their schemas, so a page never
 * has to branch on "this store has no profile yet".
 */
export const AutoPauseSchema = z.object({
  from: z.string().datetime({ offset: true }),
  to: z.string().datetime({ offset: true }).nullable(),
});
export type AutoPause = z.infer<typeof AutoPauseSchema>;

export const OrderingStateSchema = z.object({
  accepting: z.boolean(),
  pausedUntil: z.string().datetime({ offset: true }).nullable(),
  autoPause: AutoPauseSchema.optional(),
});
export type OrderingState = z.infer<typeof OrderingStateSchema>;

export const ACCEPTING_ORDERS: OrderingState = { accepting: true, pausedUntil: null };

export const MAX_PAUSE_MS = 7 * 24 * 60 * 60 * 1000;

export const SetOrderingRequestSchema = z.discriminatedUnion("accepting", [
  z.object({ accepting: z.literal(true) }),
  z.object({
    accepting: z.literal(false),
    resumeAt: z.string().datetime({ offset: true }).optional(),
  }),
]);
export type SetOrderingRequest = z.infer<typeof SetOrderingRequestSchema>;

export const ORDERS_PAUSED_CODE = "ORDERS_PAUSED";

export const PaymentSettingsSchema = z.object({
  cardEnabled: z.boolean(),
});
export type PaymentSettings = z.infer<typeof PaymentSettingsSchema>;

export const CASH_ONLY: PaymentSettings = { cardEnabled: false };

export const CARD_DISABLED_CODE = "CARD_DISABLED";

export const STORE_HUB_NAME_MAX = 40;

export const StoreHubSchema = z.object({
  deviceId: z.string().uuid(),
  computerName: z.string().min(1).max(STORE_HUB_NAME_MAX),
  key: z.string().min(1),
  setAt: z.string().datetime({ offset: true }),
});
export type StoreHub = z.infer<typeof StoreHubSchema>;

export const StoreHubResponseSchema = z.object({
  hub: StoreHubSchema.nullable(),
  timezone: TimeZoneSchema,
});
export type StoreHubResponse = z.infer<typeof StoreHubResponseSchema>;

export const SetStoreHubRequestSchema = z.object({
  deviceId: z.string().uuid(),
  computerName: z.string().trim().min(1).max(STORE_HUB_NAME_MAX),
});
export type SetStoreHubRequest = z.infer<typeof SetStoreHubRequestSchema>;

export const STORE_DEVICE_NAME_MAX = 64;
export const STORE_DEVICE_APP_VERSION_MAX = 32;

export const StoreDeviceKindSchema = z.enum(["desktop", "browser"]);
export type StoreDeviceKind = z.infer<typeof StoreDeviceKindSchema>;

const CONTROL_OR_BIDI_OVERRIDE = /[\p{Cc}\u202A-\u202E\u2066-\u2069]/u;

function deviceLabel(max: number) {
  return z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((text) => !CONTROL_OR_BIDI_OVERRIDE.test(text), "must not contain control or text-direction characters");
}

export const DeviceHandshakeSchema = z.object({
  id: z.string().uuid(),
  name: deviceLabel(STORE_DEVICE_NAME_MAX),
  kind: StoreDeviceKindSchema,
  appVersion: deviceLabel(STORE_DEVICE_APP_VERSION_MAX).nullable().optional(),
});
export type DeviceHandshake = z.infer<typeof DeviceHandshakeSchema>;

export const DeviceHandshakeAuthSchema = z.object({ device: DeviceHandshakeSchema });
export type DeviceHandshakeAuth = z.infer<typeof DeviceHandshakeAuthSchema>;

export const StoreDeviceSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(STORE_DEVICE_NAME_MAX),
  kind: StoreDeviceKindSchema,
  appVersion: z.string().nullable(),
  firstSeenAt: z.string().datetime({ offset: true }),
  lastSeenAt: z.string().datetime({ offset: true }),
  isHub: z.boolean(),
});
export type StoreDevice = z.infer<typeof StoreDeviceSchema>;

export const StoreDevicesResponseSchema = z.object({
  devices: z.array(StoreDeviceSchema),
});
export type StoreDevicesResponse = z.infer<typeof StoreDevicesResponseSchema>;

export const StoreCurrentResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(128),
  slug: z.string().min(2).max(64),
  status: StoreStatusSchema,
  landingMode: LandingModeSchema,
  profile: StoreProfileSchema,
  theme: ThemeSchema,
  ordering: OrderingStateSchema.default(ACCEPTING_ORDERS),
  payments: PaymentSettingsSchema.default(CASH_ONLY),
});
export type StoreCurrentResponse = z.infer<typeof StoreCurrentResponseSchema>;

/**
 * Body of `PUT /stores/:id/presentation`.
 *
 * Replacement, not merge: whichever of `profile` and `theme` is present is
 * written whole. A partial merge would make "clear the tagline" and "leave the
 * tagline alone" the same request, since both schemas default every field —
 * an omitted `tagline` would arrive as `null` either way and the server could
 * not tell which was meant. Sending the section you are editing in full keeps
 * that unambiguous, and an edit form has the current values already.
 *
 * At least one section must be present, so an empty body is a 400 rather than
 * a write that silently does nothing.
 */
export const UpdateStorePresentationRequestSchema = z
  .object({
    profile: StoreProfileSchema.extend({
      hourExceptions: HourExceptionListSchema.optional(),
    }).optional(),
    theme: ThemeSchema.optional(),
  })
  .refine(
    (body) => body.profile !== undefined || body.theme !== undefined,
    "provide profile, theme, or both",
  );
export type UpdateStorePresentationRequest = z.infer<
  typeof UpdateStorePresentationRequestSchema
>;

/** What that same route answers with: the store's presentation as now stored. */
export const StorePresentationSchema = z.object({
  profile: StoreProfileSchema,
  theme: ThemeSchema,
});
export type StorePresentation = z.infer<typeof StorePresentationSchema>;

export const StoreRequestSchema = z.object({
  restaurant: z.string().trim().min(2).max(60),
  contact: z.string().trim().min(2).max(80),
  email: z.string().trim().email().max(120),
  phone: z.string().trim().min(4).max(30),
  suggestedSlug: z
    .string()
    .trim()
    .max(64)
    .regex(/^[a-z0-9-]*$/, "slug must be lowercase letters, digits and hyphens only")
    .optional(),
  note: z.string().trim().max(1000).optional(),
});
export type StoreRequest = z.infer<typeof StoreRequestSchema>;

export const StoreRequestResponseSchema = z.object({
  delivered: z.literal(true),
  contactEmail: z.string().email(),
});
export type StoreRequestResponse = z.infer<typeof StoreRequestResponseSchema>;

export const StoreRequestStatusSchema = z.enum(["new", "contacted", "provisioned", "declined"]);
export type StoreRequestStatus = z.infer<typeof StoreRequestStatusSchema>;

export const PlatformStoreRequestSchema = z.object({
  id: z.string().uuid(),
  restaurant: z.string(),
  contact: z.string(),
  email: z.string(),
  phone: z.string(),
  suggestedSlug: z.string().nullable(),
  note: z.string().nullable(),
  status: StoreRequestStatusSchema,
  receivedAt: z.string(),
  statusChangedAt: z.string().nullable(),
  store: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
});
export type PlatformStoreRequest = z.infer<typeof PlatformStoreRequestSchema>;

export const UpdateStoreRequestStatusRequestSchema = z.object({
  status: z.enum(["new", "contacted", "declined"]),
});
export type UpdateStoreRequestStatusRequest = z.infer<typeof UpdateStoreRequestStatusRequestSchema>;

export const PlatformStoreOwnerSchema = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  memberSince: z.string(),
});
export type PlatformStoreOwner = z.infer<typeof PlatformStoreOwnerSchema>;

export const PlatformStoreInvitationSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  createdAt: z.string(),
  expiresAt: z.string(),
  expired: z.boolean(),
});
export type PlatformStoreInvitation = z.infer<typeof PlatformStoreInvitationSchema>;

export const PlatformStoreOwnerStateSchema = z.enum(["no_owner", "invited", "running"]);
export type PlatformStoreOwnerState = z.infer<typeof PlatformStoreOwnerStateSchema>;

/**
 * One row of the platform console's store list. Operator-facing, so unlike
 * `StoreCurrentResponse` it carries the commercial state (`plan`, `status`)
 * and who can actually run the store.
 */
export const PlatformStoreSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  plan: z.string(),
  status: StoreStatusSchema,
  statusReason: z.string().nullable(),
  statusChangedAt: z.string().nullable(),
  landingMode: LandingModeSchema,
  hostname: z.string().nullable(),
  siteUrl: z.string().url().nullable(),
  createdAt: z.string(),
  owners: z.array(PlatformStoreOwnerSchema),
  pendingInvitations: z.array(PlatformStoreInvitationSchema),
  ownerState: PlatformStoreOwnerStateSchema,
});
export type PlatformStore = z.infer<typeof PlatformStoreSchema>;

export const CUSTOM_HOSTNAME_MESSAGE = "Enter a domain, like bestkebab.dz.";

const HOSTNAME_LABEL = "[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?";
const HOSTNAME_TLD = "(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})";
const CUSTOM_HOSTNAME_PATTERN = new RegExp(
  `^(?:${HOSTNAME_LABEL}\\.)+${HOSTNAME_TLD}$`,
);
const CUSTOM_HOSTNAME_MAX = 253;

function normalizeCustomHostname(input: string): string {
  const withoutScheme = input.trim().replace(/^https?:\/\//i, "");
  const authority = withoutScheme.split(/[/?#]/, 1)[0] ?? "";
  let host = authority;
  try {
    host = new URL(`http://${authority}`).hostname;
  } catch {
    host = authority;
  }
  host = host.toLowerCase();
  return host.endsWith(".") ? host.slice(0, -1) : host;
}

export const CustomHostnameSchema = z
  .string()
  .transform(normalizeCustomHostname)
  .pipe(
    z
      .string()
      .refine(
        (host) => host.length <= CUSTOM_HOSTNAME_MAX && CUSTOM_HOSTNAME_PATTERN.test(host),
        CUSTOM_HOSTNAME_MESSAGE,
      ),
  );

export const PlatformStoreDomainSchema = z.object({
  id: z.string().uuid(),
  hostname: z.string(),
  isPlatformHost: z.boolean(),
  isPrimary: z.boolean(),
  verified: z.boolean(),
});
export type PlatformStoreDomain = z.infer<typeof PlatformStoreDomainSchema>;

export const PlatformStoreSizeSchema = z.object({
  orders: z.number().int().nonnegative(),
  menuItems: z.number().int().nonnegative(),
  staff: z.number().int().nonnegative(),
  uploadBytes: z.number().int().nonnegative().nullable(),
});
export type PlatformStoreSize = z.infer<typeof PlatformStoreSizeSchema>;

export const PlatformStoreDetailSchema = PlatformStoreSchema.extend({
  domains: z.array(PlatformStoreDomainSchema),
  size: PlatformStoreSizeSchema,
  dashboardUrl: z.string().url(),
  dnsTarget: z.string(),
});
export type PlatformStoreDetail = z.infer<typeof PlatformStoreDetailSchema>;

export const SiteStateSchema = z.enum(["live", "not_built", "unreachable"]);
export type SiteState = z.infer<typeof SiteStateSchema>;

export const StoreSiteSchema = z.object({
  hostname: z.string().nullable(),
  url: z.string().url().nullable(),
  state: SiteStateSchema,
});
export type StoreSite = z.infer<typeof StoreSiteSchema>;

export const SiteBuildStatusSchema = z.enum(["queued", "building", "live", "failed"]);
export type SiteBuildStatus = z.infer<typeof SiteBuildStatusSchema>;

export const SiteBuildSchema = z.object({
  id: z.string().uuid(),
  status: SiteBuildStatusSchema,
  hostname: z.string().nullable(),
  error: z.string().nullable(),
  requestedAt: z.string(),
  startedAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});
export type SiteBuild = z.infer<typeof SiteBuildSchema>;

export const StoreSiteBuildSchema = z.object({
  latest: SiteBuildSchema.nullable(),
  lastPublishedAt: z.string().nullable(),
});
export type StoreSiteBuild = z.infer<typeof StoreSiteBuildSchema>;

export const SiteBuildJobSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  slug: z.string(),
  hostnames: z.array(z.string()).min(1),
});
export type SiteBuildJob = z.infer<typeof SiteBuildJobSchema>;

export const FinishSiteBuildRequestSchema = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal("built") }),
  z.object({ outcome: z.literal("failed"), error: z.string() }),
]);
export type FinishSiteBuildRequest = z.infer<typeof FinishSiteBuildRequestSchema>;

export const AddStoreDomainRequestSchema = z.object({
  hostname: CustomHostnameSchema,
});
export type AddStoreDomainRequest = z.infer<typeof AddStoreDomainRequestSchema>;

export const StoreDomainProblemSchema = z.enum(["no_records", "elsewhere", "lookup_failed"]);
export type StoreDomainProblem = z.infer<typeof StoreDomainProblemSchema>;

export const StoreDomainCheckSchema = z.object({
  hostname: z.string(),
  verified: z.boolean(),
  problem: StoreDomainProblemSchema.nullable(),
  addresses: z.array(z.string()),
  expected: z.array(z.string()),
  dnsTarget: z.string(),
});
export type StoreDomainCheck = z.infer<typeof StoreDomainCheckSchema>;

export const PlatformStoresSummarySchema = z.object({
  running: z.number().int().nonnegative(),
  invited: z.number().int().nonnegative(),
  noOwner: z.number().int().nonnegative(),
  ordersToday: z.number().int().nonnegative(),
});
export type PlatformStoresSummary = z.infer<typeof PlatformStoresSummarySchema>;

export const InviteStoreOwnerRequestSchema = z.object({
  email: z.string().email(),
});
export type InviteStoreOwnerRequest = z.infer<typeof InviteStoreOwnerRequestSchema>;

export const ChangeInvitationEmailRequestSchema = z.object({
  email: z.string().email(),
});
export type ChangeInvitationEmailRequest = z.infer<typeof ChangeInvitationEmailRequestSchema>;

export const ChangeOwnerEmailRequestSchema = z.object({
  email: z.string().email(),
});
export type ChangeOwnerEmailRequest = z.infer<typeof ChangeOwnerEmailRequestSchema>;

export const ChangeOwnerEmailResponseSchema = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  linkSent: z.boolean(),
});
export type ChangeOwnerEmailResponse = z.infer<typeof ChangeOwnerEmailResponseSchema>;

export const UpdateStoreLandingModeRequestSchema = z.object({
  landingMode: LandingModeSchema,
});
export type UpdateStoreLandingModeRequest = z.infer<typeof UpdateStoreLandingModeRequestSchema>;

export const STORE_STATUS_REASON_MAX = 500;

export const UpdateStoreStatusRequestSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("blocked"),
    reason: z.string().trim().min(1).max(STORE_STATUS_REASON_MAX),
  }),
  z.object({
    status: z.literal("active"),
    reason: z.string().trim().max(STORE_STATUS_REASON_MAX).optional(),
  }),
]);
export type UpdateStoreStatusRequest = z.infer<typeof UpdateStoreStatusRequestSchema>;

export const StoreStatusResponseSchema = z.object({
  status: StoreStatusSchema,
  reason: z.string().nullable(),
  changedAt: z.string(),
});
export type StoreStatusResponse = z.infer<typeof StoreStatusResponseSchema>;

export const GRANTABLE_STAFF_ROLES = ["manager", "cashier", "kitchen"] as const;

/**
 * The roles a store's own admin may hand out. `tenant_admin` is deliberately
 * absent: an owner is minted by the platform when a store is provisioned, so
 * a store cannot grow a second owner — or lose its only one — without the
 * operator knowing.
 */
export const GrantableStaffRoleSchema = z.enum(GRANTABLE_STAFF_ROLES);
export type GrantableStaffRole = z.infer<typeof GrantableStaffRoleSchema>;

export const StaffMemberSchema = z.object({
  memberId: z.string().uuid(),
  userId: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  role: z.string(),
  memberSince: z.string(),
});
export type StaffMember = z.infer<typeof StaffMemberSchema>;

export const StaffInvitationSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  role: z.string(),
  expiresAt: z.string(),
  createdAt: z.string(),
});
export type StaffInvitation = z.infer<typeof StaffInvitationSchema>;

export const StaffListSchema = z.object({
  members: z.array(StaffMemberSchema),
  pendingInvitations: z.array(StaffInvitationSchema),
});
export type StaffList = z.infer<typeof StaffListSchema>;

export const InviteStaffRequestSchema = z.object({
  email: z.string().email(),
  role: GrantableStaffRoleSchema,
});
export type InviteStaffRequest = z.infer<typeof InviteStaffRequestSchema>;

export const ChangeStaffRoleRequestSchema = z.object({
  role: GrantableStaffRoleSchema,
});
export type ChangeStaffRoleRequest = z.infer<typeof ChangeStaffRoleRequestSchema>;

export const StartImpersonationResponseSchema = z.object({ url: z.string().url() });
export type StartImpersonationResponse = z.infer<typeof StartImpersonationResponseSchema>;

export const ImpersonationSchema = z.object({
  operator: z.object({ name: z.string(), email: z.string() }),
  expiresAt: z.string(),
});
export type Impersonation = z.infer<typeof ImpersonationSchema>;
